// Scadenzario abilitazioni con filtri, upload/download file e invio email
import { useRef, useState } from 'react'
import { Upload, Download, Send } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Select } from '../../components/ui/Select'
import { Table } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { formatDate } from '../../lib/utils'
import {
  useCertifications,
  useUploadCertification,
  useDownloadCertification,
  type Certification,
} from '../../hooks/useHse'
import { SendDocumentsModal, type DocToSend } from './components/SendDocumentsModal'

function CertRow({ cert, onSendToggle, isSelected }: {
  cert: Certification
  onSendToggle: (doc: DocToSend, checked: boolean) => void
  isSelected: boolean
}) {
  const uploadMutation = useUploadCertification(cert.id)
  const downloadMutation = useDownloadCertification()
  const fileRef = useRef<HTMLInputElement>(null)

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    uploadMutation.mutate(file)
    e.target.value = ''
  }

  const handleDownload = async () => {
    const result = await downloadMutation.mutateAsync(cert.id)
    window.open(result.url, '_blank')
  }

  const hasFile = Boolean(cert.filePath)

  return (
    <div className="flex items-center gap-2">
      {hasFile && (
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-[var(--fc-border)] accent-[var(--fc-primary)]"
          checked={isSelected}
          onChange={(e) =>
            onSendToggle(
              { type: 'certification', id: cert.id, fileName: cert.fileName ?? '', label: cert.type },
              e.target.checked,
            )
          }
        />
      )}
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleUpload} />
      {!hasFile ? (
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="flex items-center gap-1 text-xs text-[var(--fc-primary)] hover:underline disabled:opacity-50"
        >
          <Upload className="h-3 w-3" />
          {uploadMutation.isPending ? 'Caricamento...' : 'Carica'}
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="text-xs text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]"
            title="Sostituisci file"
          >
            <Upload className="h-3 w-3" />
          </button>
          <button
            onClick={handleDownload}
            disabled={downloadMutation.isPending}
            className="flex items-center gap-1 text-xs text-[var(--fc-primary)] hover:underline disabled:opacity-50"
            title={cert.fileName}
          >
            <Download className="h-3 w-3" />
            {cert.fileName ? cert.fileName.slice(0, 18) + (cert.fileName.length > 18 ? '…' : '') : 'Scarica'}
          </button>
        </div>
      )}
    </div>
  )
}

export function CertificationsPage() {
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedDocs, setSelectedDocs] = useState<DocToSend[]>([])
  const [showSendModal, setShowSendModal] = useState(false)

  const { data = [], isLoading } = useCertifications()

  const filtered = statusFilter === 'all' ? data : data.filter((c) => {
    const now = new Date()
    const expiry = c.expiryDate ? new Date(c.expiryDate) : null
    const soon = expiry ? new Date(expiry.getTime() - 30 * 86400000) : null
    const st = !expiry ? 'valid' : expiry < now ? 'expired' : (soon && soon < now) ? 'expiring' : 'valid'
    return st === statusFilter
  })

  const toggleDoc = (doc: DocToSend, checked: boolean) => {
    if (checked) {
      setSelectedDocs((prev) => [...prev.filter((d) => d.id !== doc.id), doc])
    } else {
      setSelectedDocs((prev) => prev.filter((d) => d.id !== doc.id))
    }
  }

  const columns = [
    {
      key: 'name',
      header: 'Abilitazione',
      cell: (c: Certification) => <span className="font-medium">{c.type ?? '—'}</span>,
    },
    {
      key: 'technician',
      header: 'Tecnico',
      cell: (c: Certification) => <span className="text-sm">{c.technicianId?.slice(0, 8) ?? '—'}</span>,
      className: 'w-40',
    },
    {
      key: 'issued',
      header: 'Rilascio',
      cell: (c: Certification) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">
          {c.issueDate ? formatDate(c.issueDate, 'dd/MM/yyyy') : '—'}
        </span>
      ),
      className: 'w-28',
    },
    {
      key: 'expiry',
      header: 'Scadenza',
      cell: (c: Certification) => {
        const now = new Date()
        const expiry = c.expiryDate ? new Date(c.expiryDate) : null
        const isExpired = expiry && expiry < now
        return (
          <span className={`text-sm font-medium ${isExpired ? 'text-[var(--fc-danger)]' : ''}`}>
            {c.expiryDate ? formatDate(c.expiryDate, 'dd/MM/yyyy') : '—'}
          </span>
        )
      },
      className: 'w-28',
    },
    {
      key: 'status',
      header: 'Stato',
      cell: (c: Certification) => {
        const now = new Date()
        const expiry = c.expiryDate ? new Date(c.expiryDate) : null
        const soon = expiry ? new Date(expiry.getTime() - 30 * 86400000) : null
        const st = !expiry ? 'valid' : expiry < now ? 'expired' : (soon && soon < now) ? 'expiring' : 'valid'
        return (
          <Badge variant={st === 'valid' ? 'success' : st === 'expiring' ? 'warning' : 'danger'}>
            {st === 'valid' ? 'Valido' : st === 'expiring' ? 'In scadenza' : 'Scaduto'}
          </Badge>
        )
      },
      className: 'w-28',
    },
    {
      key: 'file',
      header: 'File',
      cell: (c: Certification) => (
        <CertRow
          cert={c}
          isSelected={selectedDocs.some((d) => d.id === c.id)}
          onSendToggle={toggleDoc}
        />
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Abilitazioni"
        breadcrumb={[{ label: 'HSE', href: '/hse' }, { label: 'Abilitazioni' }]}
        actions={
          selectedDocs.length > 0 ? (
            <Button
              leftIcon={<Send className="h-4 w-4" />}
              onClick={() => setShowSendModal(true)}
            >
              Invia selezionati ({selectedDocs.length})
            </Button>
          ) : undefined
        }
      />
      <div className="w-44">
        <Select
          options={[
            { value: 'all', label: 'Tutti gli stati' },
            { value: 'valid', label: 'Valide' },
            { value: 'expiring', label: 'In scadenza' },
            { value: 'expired', label: 'Scadute' },
          ]}
          value={statusFilter}
          onValueChange={setStatusFilter}
        />
      </div>
      <Table
        data={filtered}
        columns={columns}
        keyExtractor={(c) => c.id}
        isLoading={isLoading}
        emptyMessage="Nessuna abilitazione registrata"
      />

      <SendDocumentsModal
        isOpen={showSendModal}
        onClose={() => { setShowSendModal(false); setSelectedDocs([]) }}
        preselected={selectedDocs}
      />
    </div>
  )
}
